<template>
  <nav class="h-16 px-8 flex items-center bg-transparent w-full flex-none">
    <div
      class="flex items-center gap-4 relative w-full justify-between"
      ref="tabsRef"
    >
      <!-- TABS -->
      <TransitionGroup name="scale">
        <router-link
          v-for="(tab, index) in displayedTabs"
          :key="tab.name"
          :to="{
            path: tab.route,
            query: linkQuery(tab),
          }"
          class="w-10 h-10 flex items-center justify-center rounded transition-colors !duration-500 z-[2] relative"
          :class="[
            routeIndex === index ? 'text-primary-contrast' : 'text-gray-6',
          ]"
        >
          <!-- ICON -->
          <img
            v-if="tab.href"
            :src="tab.href"
            class="w-full h-auto max-w-[24px]"
          />
          <font-awesome-icon v-else :icon="['fas', tab.icon]" />
          <!-- BADGE -->
          <span
            v-if="tab.badge && badges[tab.name]"
            class="absolute -top-1 left-[calc(100%-10px)] h-4 min-w-4 rounded-full z-10 text-center px-1 text-2xs flex items-center border border-white bg-red text-white font-bold"
          >
            {{ badges[tab.name] }}
          </span>
        </router-link>
      </TransitionGroup>
      <!-- ACTIVE BACKGROUND -->
      <div
        class="w-10 h-10 rounded absolute transition-transform flex items-center justify-center"
        :style="[activeRoute]"
        :class="
          currentRoute?.name === 'copilot'
            ? 'mayday-tab-ai-border'
            : 'bg-primary'
        "
      />
    </div>
  </nav>
</template>

<script setup lang="ts">
import { favoritesTabTypeToSectionName } from '../../utils/logs';
import {
  useNotificationStore,
  useFeedbackStore,
  useFavoritesStore,
  useCopilotStore,
  useAdminStore,
} from '@getmayday/vue-modules';
import { useAgentStore } from '@getmayday/vue-modules/agent';
import { aiIcon } from '@getmayday/vue-modules/library';

const notificationStore = useNotificationStore();
const { unreadCountDisplay } = storeToRefs(notificationStore);

const feedbackStore = useFeedbackStore();
const { unreadCommentCount } = storeToRefs(feedbackStore);

const favoritesStore = useFavoritesStore();
const { currentTabType } = storeToRefs(favoritesStore);

const copilotStore = useCopilotStore();
const { copilotLoading, copilotHasData } = storeToRefs(copilotStore);

const adminStore = useAdminStore();
const {
  hasCompanyBrainAutomationsPreference,
  hasCompanyBrainAgentsPreference,
  hasCompanyBrainAIPreference,
} = storeToRefs(adminStore);

const agentStore = useAgentStore();
const { availableAgents } = storeToRefs(agentStore);

const { appSectionView } = useLogs();

const shouldDisplayCopilot = computed(() => {
  return (
    hasCompanyBrainAgentsPreference.value &&
    !!availableAgents.value?.length &&
    hasCompanyBrainAIPreference.value
  );
});

type Tab = {
  icon?: string;
  name: string;
  route: string;
  href?: string;
  badge?: boolean;
  display: boolean;
  query?: Record<string, string>;
};

const tabs = computed((): Tab[] => [
  {
    icon: 'book',
    name: 'knowledge',
    route: '/',
    display: true,
  },
  {
    route: '/copilot',
    name: 'copilot',
    href: aiIcon,
    display: shouldDisplayCopilot.value,
  },
  {
    icon: 'grid-2-plus',
    name: 'favorites',
    route: '/favorites',
    display: true,
  },
  {
    icon: 'bell',
    name: 'notifications',
    route: '/notifications',
    badge: true,
    display: true,
  },
  {
    icon: 'megaphone',
    name: 'feedbacks',
    route: '/feedbacks',
    query: {
      source: 'feedback_section',
    },
    badge: true,
    display: true,
  },
]);

const emit = defineEmits(['expand-copilot']);

const badges = computed<Record<string, string | null>>(() => {
  return {
    notifications: unreadCountDisplay.value,
    feedbacks:
      unreadCommentCount.value > 0 ? String(unreadCommentCount.value) : null,
  };
});

const displayedTabs = computed(() => {
  return tabs.value.filter((tab) => tab.display);
});

// CHANGE ROUTE ANIMATION
const ANIMATION_DURATION = 500;
const tabsRef = ref<HTMLElement | null>(null);
const route = useRoute();

const routeIndex = computed(() => {
  if (!route?.matched?.length) return 0;
  const index = displayedTabs.value.findIndex(
    (tab) => tab.route === route.matched[0].path,
  );
  return index < 0 ? 0 : index;
});

const currentRoute = computed(() => {
  return displayedTabs.value[routeIndex.value];
});

const animationDuration = ref(ANIMATION_DURATION);

const computedAnimationDuration = computed(() => {
  return animationDuration.value;
});

const activeRoute = computed(() => {
  if (!tabsRef.value) return 'transform: translateX(-200%)';
  if (routeIndex.value === -1 || !displayedTabs.value[routeIndex.value])
    return 'transform: translateX(-200%)';

  const element = tabsRef.value?.children[routeIndex.value] as HTMLElement;
  const elementLeft = element?.offsetLeft || 0;

  return `transform: translateX(${elementLeft}px); transition-duration: ${computedAnimationDuration.value}ms !important;`;
});

const linkQuery = (tab: Tab) => {
  return {
    ...(route.query.queryText ? { queryText: route.query.queryText } : {}),
    ...(tab.query ? { ...tab.query } : {}),
  };
};

watch(routeIndex, (newValue) => {
  const sectionName = currentRoute.value?.name;
  if (sectionName !== 'favorites') {
    appSectionView(sectionName.toUpperCase());
  } else if (currentTabType.value) {
    appSectionView(
      favoritesTabTypeToSectionName[currentTabType.value].toUpperCase(),
    );
  }
});

watch(currentTabType, (newValue) => {
  if (newValue) {
    appSectionView(favoritesTabTypeToSectionName[newValue].toUpperCase());
  }
});
</script>

<style lang="postcss" scoped>
.router-link-active {
  color: theme('colors.primary.contrast');
}

.shadow-tabs {
  box-shadow: 0px -1px 10px rgba(112, 144, 176, 0.15);
}

.transition-text-color {
  transition-property: color;
  transition-duration: 500ms;
}
.mayday-tab-ai-border::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 4px;
  padding: 2px;
  background: linear-gradient(
    110deg,
    rgba(131, 195, 229, 1),
    rgba(224, 147, 244, 1),
    rgba(235, 77, 114, 1),
    rgba(242, 180, 77, 1)
  );

  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;

  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask-composite: exclude;
  filter: drop-shadow(0 0 6px rgba(131, 195, 229, 0.6));
}
</style>
